import { AnalyticsService } from '../../../service/analytics.service';
import {
  Component,
  OnInit,
  ChangeDetectorRef
} from '@angular/core';

import {
  CommonModule
} from '@angular/common';

import {
  Router
} from '@angular/router';

import Swal from 'sweetalert2';

import {
  CheckoutService,
  CheckoutData,
  PacoteSelecionado
} from '../../../service/checkout.service';

import {
  PacoteAdicionalService,
  PacoteAdicional
} from '../../../service/pacote-adicional.service';


@Component({
  selector: 'app-checkout-pacotes',

  standalone: true,

  imports: [
    CommonModule
  ],

  templateUrl: './checkout-pacotes.component.html',

  styleUrl: './checkout-pacotes.component.css'
})
export class CheckoutPacotesComponent
  implements OnInit {


  dados: CheckoutData | null = null;

  pacotes: PacoteAdicional[] = [];

  pacotesSelecionados: PacoteSelecionado[] = [];

  imagemAtualPorPacote: { [pacoteId: number]: number } = {};

  carregando = true;

  constructor(
    private checkoutService: CheckoutService,
    private pacoteService: PacoteAdicionalService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private analyticsService: AnalyticsService
  ) {}


  ngOnInit(): void {

    this.carregarDados();

  }


  /* =====================================================
     CARREGAR DADOS DO CHECKOUT
  ===================================================== */

  carregarDados(): void {

    this.dados =
      this.checkoutService.obter();


    /*
     * O checkout é dinâmico.
     *
     * Não verificamos nenhuma acomodação específica.
     *
     * Apenas precisamos ter um quartoId para saber
     * quais pacotes pertencem à acomodação selecionada.
     */

    if (
      !this.dados ||
      !this.dados.quartoId
    ) {

      Swal.fire({

        icon: 'warning',

        title: 'Reserva não encontrada',

        text:
          'Os dados da sua reserva não foram encontrados.',

        confirmButtonText: 'Voltar',

        confirmButtonColor: '#596238',

        customClass: {
          popup: 'swal-cabana'
        }

      }).then(() => {

        /*
         * Volta para a etapa anterior do checkout.
         *
         * Não existe nenhuma acomodação fixa aqui.
         */

        this.router.navigate([
          '/checkout/hospedes'
        ]);

      });

      return;

    }


    /*
     * Recupera os pacotes já selecionados,
     * caso o cliente tenha voltado para essa etapa.
     */

    this.pacotesSelecionados =
      this.dados.pacotesSelecionados
        ? [...this.dados.pacotesSelecionados]
        : [];


    this.carregarPacotes();

  }


  /* =====================================================
     CARREGAR PACOTES DA ACOMODAÇÃO
  ===================================================== */

  carregarPacotes(): void {

    const quartoId =
      this.dados?.quartoId;


    if (!quartoId) {

      this.carregando = false;

      return;

    }


    this.carregando = true;


    this.pacoteService
      .listarTodos()
      .subscribe({

        next: (pacotes) => {

          /*
           * Mostra somente:
           *
           * 1. pacotes ativos
           * 2. pacotes pertencentes ao quarto selecionado
           */

          this.pacotes =
            pacotes.filter(pacote =>
              pacote.ativo &&
              pacote.quartoId === quartoId
            );


          this.carregando = false;

          this.cdr.detectChanges();

        },


        error: (erro) => {

          console.error(
            'Erro ao carregar pacotes:',
            erro
          );


          this.pacotes = [];

          this.carregando = false;

          this.cdr.detectChanges();


          Swal.fire({

            icon: 'error',

            title: 'Erro ao carregar pacotes',

            text:
              'Não foi possível carregar os pacotes adicionais. Você poderá continuar sem adicionar nenhum pacote.',

            confirmButtonText: 'Continuar',

            confirmButtonColor: '#596238',

            customClass: {
              popup: 'swal-cabana'
            }

          });

        }

      });

  }


  /* =====================================================
     VERIFICAR SE O PACOTE ESTÁ SELECIONADO
  ===================================================== */

  estaSelecionado(
    pacoteId: number
  ): boolean {

    return this.pacotesSelecionados
      .some(pacote =>
        pacote.id === pacoteId
      );

  }


  /* =====================================================
     ADICIONAR / REMOVER PACOTE
  ===================================================== */

  alternarPacote(
    pacote: PacoteAdicional
  ): void {

    const index =
      this.pacotesSelecionados.findIndex(
        selecionado =>
          selecionado.id === pacote.id
      );


    if (index >= 0) {

      /*
       * Remove o pacote.
       */

      this.pacotesSelecionados
        .splice(index, 1);

    } else {

      /*
       * Adiciona o pacote.
       */

      this.pacotesSelecionados.push({

        id: pacote.id,

        nome: pacote.nome,

        valor: Number(pacote.valor || 0)

      });

    }


    this.atualizarValorPacotes();

    // Registra a alteração na seleção dos pacotes
    this.analyticsService.registrarEvento(
      'selecionou_pacote',
      {
        quarto_id: String(this.dados?.quartoId ?? ''),
        pacote_id: String(pacote.id),
        pacote_nome: pacote.nome,
        acao: index >= 0 ? 'removido' : 'adicionado',
        quantidade_pacotes: this.pacotesSelecionados.length,
        valor_pacotes: this.valorPacotes
      }
    );

    this.cdr.detectChanges();

  }


  /* =====================================================
     ATUALIZAR VALOR DOS PACOTES
  ===================================================== */

  atualizarValorPacotes(): void {

    const valorPacotes =
      this.pacotesSelecionados.reduce(
        (total, pacote) =>
          total + Number(pacote.valor || 0),

        0
      );


    this.checkoutService.atualizar({

      pacotesSelecionados:
        [...this.pacotesSelecionados],

      valorPacotes

    });


    this.dados =
      this.checkoutService.obter();

  }


  /* =====================================================
     VALOR DOS PACOTES
  ===================================================== */

  get valorPacotes(): number {

    return this.pacotesSelecionados.reduce(
      (total, pacote) =>
        total + Number(pacote.valor || 0),

      0
    );

  }


  /* =====================================================
     VALOR DA HOSPEDAGEM
  ===================================================== */

  get valorHospedagem(): number {

    return this.dados?.valorTotal ?? 0;

  }


  /* =====================================================
     TOTAL COM PACOTES
  ===================================================== */

  get valorTotalComPacotes(): number {

    return (
      this.valorHospedagem +
      this.valorPacotes
    );

  }


  /* =====================================================
     IMAGEM DO PACOTE
  ===================================================== */

  /* =====================================================
   IMAGEM ATUAL DO PACOTE
===================================================== */

  getImagemAtual(
    pacote: PacoteAdicional
  ): string {

    if (
      !pacote.imagens ||
      pacote.imagens.length === 0
    ) {
      return '';
    }

    const imagens =
      [...pacote.imagens]
        .sort((a, b) =>
          a.ordem - b.ordem
        );

    const indice =
      this.imagemAtualPorPacote[pacote.id] ?? 0;

    const imagem =
      imagens[indice];

    if (!imagem) {
      return '';
    }

    return this.pacoteService
      .getImagemUrl(imagem.url);
  }

  /* =====================================================
   IMAGEM ANTERIOR
===================================================== */

  imagemAnterior(
    pacote: PacoteAdicional,
    event: Event
  ): void {

    event.stopPropagation();

    if (
      !pacote.imagens ||
      pacote.imagens.length <= 1
    ) {
      return;
    }

    const total =
      pacote.imagens.length;

    const atual =
      this.imagemAtualPorPacote[pacote.id] ?? 0;

    this.imagemAtualPorPacote[pacote.id] =
      atual === 0
        ? total - 1
        : atual - 1;

    this.cdr.detectChanges();
  }

  /* =====================================================
   PRÓXIMA IMAGEM
===================================================== */

  proximaImagem(
    pacote: PacoteAdicional,
    event: Event
  ): void {

    event.stopPropagation();

    if (
      !pacote.imagens ||
      pacote.imagens.length <= 1
    ) {
      return;
    }

    const total =
      pacote.imagens.length;

    const atual =
      this.imagemAtualPorPacote[pacote.id] ?? 0;

    this.imagemAtualPorPacote[pacote.id] =
      atual === total - 1
        ? 0
        : atual + 1;

    this.cdr.detectChanges();
  }

  /* =====================================================
   SELECIONAR IMAGEM
===================================================== */

  selecionarImagem(
    pacote: PacoteAdicional,
    indice: number,
    event: Event
  ): void {

    event.stopPropagation();

    this.imagemAtualPorPacote[pacote.id] =
      indice;

    this.cdr.detectChanges();
  }

  /* =====================================================
     FORMATAR VALOR
  ===================================================== */

  formatarValor(
    valor: number
  ): string {

    return new Intl.NumberFormat(
      'pt-BR',
      {
        style: 'currency',
        currency: 'BRL'
      }
    ).format(valor);

  }

// =====================================================
// DADOS DO RESUMO
// =====================================================

get adultos(): number {

  return this.dados?.adultos ?? 0;

}


get criancas(): number {

  return this.dados?.criancas ?? 0;

}


get animais(): number {

  return this.dados?.animais ?? 0;

}


formatarData(
  data: string | undefined
): string {

  if (!data) {

    return '';

  }


  const partes =
    data.split('-');


  if (partes.length !== 3) {

    return data;

  }


  return (
    `${partes[2]}/` +
  `${partes[1]}/` +
`${partes[0]}`
);

}


get descricaoAcomodacao(): string {

  if (this.dados?.quartoId === 1) {

    return 'Cabana confortável em meio à natureza';

  }


  if (this.dados?.quartoId === 2) {

    return 'Um pedaço da Grécia em meio ao Cerrado de Goiás';

  }


  return 'Acomodação no Paraíso do Cerrado';

}



  /* =====================================================
     CONTINUAR
  ===================================================== */

  continuar(): void {

    const valorHospedagem =
      Number(this.dados?.valorTotal ?? 0);

    const valorPacotes =
      this.valorPacotes;

    const valorTotal =
      valorHospedagem +
      valorPacotes;


    this.checkoutService.atualizar({

      pacotesSelecionados: [
        ...this.pacotesSelecionados
      ],

      valorPacotes,

      valorTotal

    });


    this.dados =
      this.checkoutService.obter();

    // Registra que o cliente avançou para a etapa de pagamento
    this.analyticsService.registrarEvento(
      'add_payment_info',
      {
        currency: 'BRL',
        value: Number(this.dados?.valorTotal ?? 0),
        quarto_id: String(this.dados?.quartoId ?? ''),
        quantidade_pacotes: this.pacotesSelecionados.length
      }
    );


    this.router.navigate([
      '/checkout/pagamento'
    ]);

  }


  /* =====================================================
     VOLTAR
  ===================================================== */

  voltar(): void {

    this.router.navigate([
      '/checkout/hospedes'
    ]);

  }


  /* =====================================================
     QUANTIDADE DE NOITES
  ===================================================== */

  get quantidadeNoites(): number {

    return this.dados?.quantidadeNoites ?? 0;

  }


  /* =====================================================
     NOME DA ACOMODAÇÃO
  ===================================================== */

  get nomeAcomodacao(): string {

    return (
      this.dados?.quartoNome ||
      'Sua acomodação'
    );

  }

}
